import { API_URL } from './config';

class ApiError extends Error {
  constructor(message, code, status) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.status = status;
  }
}

let authToken = null;
const AUTH_TOKEN_STORAGE_KEY = 'rebelprompts_session_token';

const readStoredAuthToken = () => {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage.getItem(AUTH_TOKEN_STORAGE_KEY);
  } catch (error) {
    return null;
  }
};

authToken = readStoredAuthToken();

export const setAuthToken = (token) => {
  authToken = token;
  if (typeof window === 'undefined') return;
  try {
    if (token) {
      window.localStorage.setItem(AUTH_TOKEN_STORAGE_KEY, token);
    } else {
      window.localStorage.removeItem(AUTH_TOKEN_STORAGE_KEY);
    }
  } catch (error) {
    // Ignore storage failures and keep in-memory auth working.
  }
};

export const clearAuthToken = () => {
  setAuthToken(null);
};

// Read CSRF token from cookie
const getCsrfToken = () => {
  const match = document.cookie.match(/(?:^|;\s*)csrf_token=([^;]*)/);
  return match ? decodeURIComponent(match[1]) : null;
};

const buildUrl = (path, query) => {
  const base = API_URL.replace(/\/$/, '');
  const normalized = path.startsWith('http') ? path : `${base}${path.startsWith('/') ? '' : '/'}${path}`;
  const url = new URL(normalized);
  if (query) {
    Object.entries(query).forEach(([key, value]) => {
      if (value === undefined || value === null) return;
      url.searchParams.set(key, String(value));
    });
  }
  return url.toString();
};

const parseJson = async (response) => {
  const contentType = response.headers.get('content-type') || '';
  if (!contentType.includes('application/json')) return null;
  try {
    return await response.json();
  } catch (err) {
    return null;
  }
};

const request = async (path, options = {}) => {
  const url = buildUrl(path, options.query);
  const headers = { ...(options.headers || {}) };
  const token = options.authToken ?? authToken;
  const timeoutMs = options.timeoutMs ?? 12000;
  const method = (options.method || 'GET').toUpperCase();

  const isFormData = typeof FormData !== 'undefined' && options.body instanceof FormData;
  if (options.body && !isFormData) {
    headers['Content-Type'] = headers['Content-Type'] || 'application/json';
  }
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  
  // Add CSRF token for state-changing requests
  if (['POST', 'PUT', 'PATCH', 'DELETE'].includes(method)) {
    const csrfToken = getCsrfToken();
    if (csrfToken) {
      headers['X-CSRF-Token'] = csrfToken;
    }
  }

  const controller = options.signal ? null : new AbortController();
  const signal = options.signal || controller?.signal;
  let timeoutId;
  if (controller) {
    timeoutId = setTimeout(() => controller.abort(), timeoutMs);
  }

  let response;
  try {
    response = await fetch(url, {
      method,
      headers,
      credentials: options.credentials ?? 'include',
      body: options.body ? (isFormData ? options.body : JSON.stringify(options.body)) : undefined,
      signal
    });
  } catch (error) {
    if (controller && error?.name === 'AbortError') {
      throw new ApiError('Request timed out', 'TIMEOUT', 408);
    }
    throw error;
  } finally {
    if (timeoutId) clearTimeout(timeoutId);
  }

  const payload = await parseJson(response);
  if (!response.ok) {
    const error = payload?.error || payload?.detail;
    let message;
    if (Array.isArray(error)) {
      // Handle Pydantic validation errors (array of {msg, loc, type})
      message = error.map(e => e.msg || e.message || String(e)).join('. ');
    } else if (typeof error === 'object' && error !== null) {
      message = error.message || error.msg || JSON.stringify(error);
    } else {
      message = error || response.statusText || 'Request failed';
    }
    const code = (typeof error === 'object' && error?.code) || 'HTTP_ERROR';
    throw new ApiError(message, code, response.status);
  }

  const sessionToken = payload?.data?.session_token;
  if (typeof sessionToken === 'string' && sessionToken.trim()) {
    setAuthToken(sessionToken.trim());
  }

  if (payload && typeof payload === 'object' && 'data' in payload) {
    return payload;
  }

  return { data: payload, error: null, meta: {} };
};

const get = async (path, query) => {
  const res = await request(path, { method: 'GET', query });
  return res.data;
};

const post = async (path, body) => {
  const res = await request(path, { method: 'POST', body });
  return res.data;
};

const patch = async (path, body) => {
  const res = await request(path, { method: 'PATCH', body });
  return res.data;
};

const put = async (path, body) => {
  const res = await request(path, { method: 'PUT', body });
  return res.data;
};

const del = async (path, body) => {
  const res = await request(path, { method: 'DELETE', body });
  return res.data;
};

export const apiClient = {
  request,
  get,
  post,
  put,
  patch,
  del,
  delete: del
};

export { ApiError };
